import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Form, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import './Calculator.css';

const Calculator = () => {
    const { carId } = useParams();
    const [carDetails, setCarDetails] = useState(null);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState("");
    const [selectedPort, setSelectedPort] = useState("");
    const [freight, setFreight] = useState(null);
    const [inspectionCharge, setInspectionCharge] = useState(null);
    const [hasInspection, setHasInspection] = useState(false);
    const [includeInspection, setIncludeInspection] = useState(false);
    const [totalCNFPrice, setTotalCNFPrice] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paymentType, setPaymentType] = useState("");
    const [isConfirmed, setIsConfirmed] = useState("");
    const [yesChecked, setYesChecked] = useState(false);
    const [noChecked, setNoChecked] = useState(false);
    const [yesNo, setYesNo] = useState("");


    // State for popup and form data
    const [showPopup, setShowPopup] = useState(false);
    const [formData, setFormData] = useState({
        fullName: "",
        phoneNumber: "",
        email: "",
        country: ""
    });

    // Fetch car details
    useEffect(() => {
        setLoading(true);
        fetch(`https://api.mitradingjp.com/get-car-details-with-images.php?id=${carId}`)
            .then(response => response.json())
            .then(data => {
                setCarDetails(data.carDetails);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching car details:", error);
                setLoading(false);
            });
    }, [carId]);

    // Fetch countries and ports
    useEffect(() => {
        fetch("https://api.mitradingjp.com/get_countries_ports.php")
            .then(response => response.json())
            .then(data => {
                setCountries(data.countries || []);
            })
            .catch(error => {
                console.error("Error fetching countries:", error);
            });
    }, []);

    // Update ports, freight, and total when country changes
    useEffect(() => {
        setSelectedPort("");
        setFreight(null);
        setInspectionCharge(null);
        setHasInspection(false);
        setIncludeInspection(false);
        setTotalCNFPrice(null);
        if (selectedCountry && carDetails && carDetails.m3 && carDetails.price) {
            const countryData = countries.find(c => c.country === selectedCountry);
            if (countryData && countryData.destination_ports && countryData.destination_ports.length > 0) {
                setSelectedPort(countryData.destination_ports[0].port);
                setFreight(Math.round(carDetails.m3 * (countryData.destination_ports[0].rate || 0)));
                setInspectionCharge(Math.round(countryData.inspection_charge || 0));
                setHasInspection(countryData.has_inspection || 0);
                calculateTotalCNFPrice(
                    carDetails.price,
                    carDetails.m3,
                    countryData.destination_ports[0].rate || 0,
                    countryData.inspection_charge || 0,
                    countryData.has_inspection || 0
                );
            }
        }
    }, [selectedCountry, countries, carDetails]);

    // Update freight and total when port changes
    useEffect(() => {
        if (selectedPort && carDetails && carDetails.m3 && carDetails.price) {
            const countryData = countries.find(c => c.country === selectedCountry);
            if (countryData) {
                const portData = countryData.destination_ports.find(p => p.port === selectedPort);
                if (portData) {
                    setFreight(Math.round(carDetails.m3 * (portData.rate || 0)));
                    setInspectionCharge(Math.round(countryData.inspection_charge || 0));
                    calculateTotalCNFPrice(
                        carDetails.price,
                        carDetails.m3,
                        portData.rate || 0,
                        countryData.inspection_charge || 0,
                        countryData.has_inspection || 0
                    );
                }
            }
        }
    }, [selectedPort, carDetails, selectedCountry, countries]);

    const calculateTotalCNFPrice = (price, m3, rate, inspectionCharge, hasInspection) => {
        if (price && m3 && rate !== null) {
            const freightValue = m3 * rate;
            const total = parseFloat(price) + freightValue + (hasInspection > 0 ? parseFloat(inspectionCharge) : 0);
            setTotalCNFPrice(Math.round(total));
        } else {
            setTotalCNFPrice(null);
        }
    };

    // Handle manual calculation via button
    const handleCalculate = () => {
        if (carDetails && carDetails.m3 && carDetails.price && selectedPort) {
            const countryData = countries.find(c => c.country === selectedCountry);
            if (countryData) {
                const portData = countryData.destination_ports.find(p => p.port === selectedPort);
                if (portData) {
                    setFreight(Math.round(carDetails.m3 * (portData.rate || 0)));
                    setInspectionCharge(Math.round(countryData.inspection_charge || 0));
                    calculateTotalCNFPrice(
                        carDetails.price,
                        carDetails.m3,
                        portData.rate || 0,
                        countryData.inspection_charge || 0,
                        countryData.has_inspection || 0
                    );
                }
            }
        } else {
            alert("Please select a country and port to calculate.");
        }
    };

    if (!carDetails) {
        return <div>Loading... <Spinner animation="border" size="sm" /></div>;
    }

    const features = JSON.parse(carDetails.features || "{}");

    // Function to render star ratings
    const renderStars = (rating) => {
        const maxStars = 5;
        const filledStars = rating ? parseInt(rating) : 0;
        const emptyStars = maxStars - filledStars;
        return (
            <>
                {[...Array(filledStars)].map((_, i) => (
                    <span key={`filled-${i}`} style={{ color: 'gold' }}>★</span>
                ))}
                {[...Array(emptyStars)].map((_, i) => (
                    <span key={`empty-${i}`} style={{ color: 'gray' }}>☆</span>
                ))}
            </>
        );
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Handle form submission
    const handleSend = () => {
        const { fullName, phoneNumber, email, country } = formData;
        if (fullName && phoneNumber && email && country) {
            const message = `Name: ${fullName}\nPhone/WhatsApp: ${phoneNumber}\nEmail: ${email}\nCountry: ${country}\nCar: ${carDetails.maker} ${carDetails.model} (Stock ID: ${carDetails.stock_id})\nTotal CNF Price: $${totalCNFPrice || 'N/A'}`;
            const whatsappUrl = `https://wa.me/819081000786?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
            setShowPopup(false); // Close popup after sending
            setFormData({ fullName: "", phoneNumber: "", email: "", country: "" }); // Reset form
        } else {
            alert("Please fill out all fields.");
        }
    };

    // Handle popup close
    const handleClosePopup = () => {
        setShowPopup(false);
        setFormData({ fullName: "", phoneNumber: "", email: "", country: "" }); // Reset form on close
    };

    return (
        <div className="container-fluids">
            <div className="row justify-content-centers">
                <div className="s">
                    <div className="calculator-container">
                        <div className="vehicle-header">
                            <p className="mb-0">Stock Number: {carDetails.stock_id}</p>
                            <h3 className="mb-0">{carDetails.year} - {carDetails.maker} {carDetails.model} </h3>
                        </div>

                        <div className="mb-3 price-box d-none">
                            <label className="form-label">Car Price (FOB)</label>
                            <div className="input-groups">
                                <span className="input-groups-text">${carDetails.price}</span>
                            </div>
                        </div>

                        <div className="row mb-2 border-bottom p-2">
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>Mileage:</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>{carDetails.mileage} km</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>Engine:</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                    <div className="car_det">
                                    <p>{carDetails.engine} cc</p>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-2 border-bottom p-2">
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>Transmission:</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>{carDetails.transmission}</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>Drive:</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                    <div className="car_det">
                                    <p>{carDetails.drive}</p>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-2 border-bottom p-2">
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>Steering:</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>{carDetails.steering}</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>Fuel:</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                    <div className="car_det">
                                    <p>{carDetails.fuel}</p>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-2 border-bottom p-2">
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>Doors:</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>{carDetails.doors}</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="car_det">
                                    <p>Seats:</p>
                                </div>
                            </div>
                            <div className="col-md-3">
                                    <div className="car_det">
                                    <p>{carDetails.seats}</p>
                                </div>
                            </div>
                        </div>


                        <Form.Group className="mb-3">
                            <Form.Label className="all-label">Country</Form.Label>
                            <Form.Select
                                value={selectedCountry}
                                onChange={(e) => setSelectedCountry(e.target.value)}
                            >
                                <option value="" disabled>Select country</option>
                                {countries.map(country => (
                                    <option key={country.id} value={country.country}>
                                        {country.country}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="all-label">Destination Port</Form.Label>
                            <Form.Select
                                value={selectedPort}
                                onChange={(e) => setSelectedPort(e.target.value)}
                                disabled={!selectedCountry}
                            >
                                <option value="" disabled>Select port</option>
                                {selectedCountry && countries
                                    .find(c => c.country === selectedCountry)
                                    ?.destination_ports.map((port, index) => (
                                        <option key={index} value={port.port}>
                                            {port.port}
                                        </option>
                                    ))}
                            </Form.Select>
                            {freight !== null && (
                                <div className="mt-4 price-box">
                                    <label className="form-label">Freight</label>
                                    <div className="input-groups">
                                        <span className="input-groups-text">${freight}</span>
                                    </div>
                                </div>
                            )}
                            {inspectionCharge !== null && hasInspection > 0 && (
                                <div className="mt-2 price-box">
                                    <label className="form-label">Inspection Charge</label>
                                    <div className="input-groups">
                                        <span className="input-groups-text">${inspectionCharge}</span>
                                    </div>
                                </div>
                            )}
                            {totalCNFPrice !== null && (
                                <div className="mt-2 mb-4 price-box">
                                    <label className="form-label">Total CNF Price</label>
                                    <div className="input-groups">
                                        <span className="input-groups-text">${totalCNFPrice}</span>
                                    </div>
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-4">
                        <Form.Label className="all-label">Freight</Form.Label>
                            <div className="d-flex gap-4">
                                <Form.Check
                                    type="checkbox"
                                    label="Prepaid"
                                    checked={paymentType === "prepaid"}
                                    onChange={() =>
                                        setPaymentType(paymentType === "prepaid" ? "" : "prepaid")
                                    }
                                />
                                <Form.Check
                                    type="checkbox"
                                    label="Collect"
                                    checked={paymentType === "collect"}
                                    onChange={() =>
                                        setPaymentType(paymentType === "collect" ? "" : "collect")
                                    }
                                />
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="all-label">Inspection</Form.Label>

                            <div className="d-flex gap-4">
                                <Form.Check
                                    type="checkbox"
                                    label="Yes"
                                    checked={yesChecked}
                                    onChange={(e) => setYesChecked(e.target.checked)}
                                />

                                <Form.Check
                                    type="checkbox"
                                    label="No"
                                    checked={noChecked}
                                    onChange={(e) => setNoChecked(e.target.checked)}
                                />
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-4">
                        <Form.Label className="all-label">Insurance</Form.Label>
                            <div className="d-flex gap-4">
                                <Form.Check
                                    type="checkbox"
                                    label="Yes"
                                    checked={yesNo === "yes"}
                                    onChange={() =>
                                        setYesNo(yesNo === "yes" ? "" : "yes")
                                    }
                                />
                                <Form.Check
                                    type="checkbox"
                                    label="No"
                                    checked={yesNo === "no"}
                                    onChange={() =>
                                        setYesNo(yesNo === "no" ? "" : "no")
                                    }
                                />
                            </div>
                        </Form.Group>


                        <button className="btn btn-success w-100 py-2" onClick={() => setShowPopup(true)}>
                            Get A Free Quote
                        </button>

                        {showPopup && (
                            <div className="popup-overlay" onClick={handleClosePopup}>
                                <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                                    <h4>Request a Quote</h4>
                                    <p>Kindly fill out the form below with your details. Our dedicated sales agent will review your request and get in touch with you as soon as possible.</p>
                                    <Form>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Full Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="fullName"
                                                value={formData.fullName}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Phone / WhatsApp Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phoneNumber"
                                                value={formData.phoneNumber}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email Address</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Country</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                        <button className="btn btn-primary w-100" onClick={handleSend}>
                                            Send
                                        </button>
                                        <button className="btn btn-secondary w-100 mt-2" onClick={handleClosePopup}>
                                            Cancel
                                        </button>
                                    </Form>
                                </div>
                            </div>
                        )}

                        <div className="text-center text-muted mb-3">
                            <small>Completed in 1 minute</small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Basic CSS for popup */}
            <style>
                {`
                    .popup-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background: rgba(0, 0, 0, 0.5);
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        z-index: 1000;
                    }
                    .popup-content {
                        background: #0E3435;
                        padding: 20px;
                        border-radius: 8px;
                        width: 90%;
                        max-width: 400px;
                        color: #F4CA82;
                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    }
                    .popup-content h4 {
                        margin-top: 0;
                        color: #F4CA82;
                    }
                    .popup-content p {
                        margin-bottom: 20px;
                        color: #F4CA82;
                    }
                    .popup-content .form-label {
                        color: #F4CA82;
                    }
                `}
            </style>
        </div>
    );
};

export default Calculator;